import * as sqliteCore from "@-/com.npmjs.drizzle-orm/sqlite-core";
import { SQLiteIntegerBuilder } from "@-/com.npmjs.drizzle-orm/sqlite-core/columns/integer";

import type {
  BlobConfig,
  IntegerConfig,
  SQLiteTable,
  SQLiteTextConfig,
} from "@-/com.npmjs.drizzle-orm/sqlite-core";

// On iOS, expo-sqlite serializes SQLite INTEGER values as JS strings to dodge
// INT64 overflow in the JSI bridge. Drizzle's base `Column` class installs
// `mapFromDriverValue = noop` as an instance field, so a prototype-level
// override on `SQLiteInteger` is shadowed at construction time. Instead, hook
// `SQLiteIntegerBuilder.build()` so every newly-built integer column gets a
// coercing `mapFromDriverValue` written onto the instance. Without this the
// row shape for an `integer()` column is `string` at runtime — even though
// the declared TS type is `number` — which breaks consumers that test
// `typeof value === "number"` (e.g. `useCardIdToFrontDict`).
const originalBuild = SQLiteIntegerBuilder.prototype.build;
Object.assign(SQLiteIntegerBuilder.prototype, {
  build(table: SQLiteTable) {
    const column = originalBuild.call(this, table);
    Object.assign(column, {
      mapFromDriverValue(value: unknown): unknown {
        if (typeof value === "string" && value !== "" && Number.isFinite(Number(value))) {
          return Number(value);
        }
        return value;
      },
    });
    return column;
  },
});

export type LowercaseDatabaseName<Name extends string> =
  Name extends Lowercase<Name> ? Name : never;

export function integer<const Name extends string, TMode extends IntegerConfig["mode"]>(
  name: LowercaseDatabaseName<Name>,
  config?: IntegerConfig<TMode>,
) {
  return sqliteCore.integer(name, config);
}

export function real<const Name extends string>(name: LowercaseDatabaseName<Name>) {
  return sqliteCore.real(name);
}

export function text<
  const Name extends string,
  U extends string,
  T extends Readonly<[U, ...U[]]>,
  TMode extends "text" | "json" = "text" | "json",
>(name: LowercaseDatabaseName<Name>, config?: SQLiteTextConfig<TMode, T | [...T]>) {
  return sqliteCore.text(name, config);
}

export function blob<const Name extends string, TMode extends BlobConfig["mode"]>(
  name: LowercaseDatabaseName<Name>,
  config?: BlobConfig<TMode>,
) {
  return sqliteCore.blob(name, config);
}

export { index, uniqueIndex } from "@-/com.npmjs.drizzle-orm/sqlite-core";
